import { ZodError } from 'zod'

import { UnauthenticatedApi } from '@mondough/public-api-request'

import * as Api from './api'
// Reimport required to be able to mock the exported isBrowserRuntime
// see https://stackoverflow.com/a/55193363
import { isBrowserRuntime as isBrowser } from './server'
import type { CopyBundle, OAuthClient } from './types'

export type { CopyBundle } from './types'

/**
 * Server-side loader for fetching the specified copy bundle from `s.api.copy`
 */
export async function loadCopyServerSide(
  appName: string,
  client: OAuthClient,
): Promise<CopyBundle | null> {
  // Call the self-imported reference instead of the local one so that
  // mocking works when testing
  if (isBrowser()) {
    return null
  }

  // normalise api URL
  client.apiHost = client.apiHost.endsWith('/')
    ? client.apiHost.slice(0, -1)
    : client.apiHost

  console.info(
    `[lib.copy] Attempting to fetch bundle '${appName}' from ${client.apiHost}`,
  )

  const appNameFromEnv = process.env.NEXT_PUBLIC_SERVICE_NAME
  if (appNameFromEnv && appName !== appNameFromEnv) {
    console.warn(
      `[lib.copy] Found an appName environment variable (NEXT_PUBLIC_SERVICE_NAME) which is different to the value supplied to loadCopyServerSide. NEXT_PUBLIC_SERVICE_NAME='${appNameFromEnv}' vs. appName='${appName}'. Double check that you're supplying the correct value, and favour the NEXT_PUBLIC_SERVICE_NAME env variable if you can.`,
    )
  }

  try {
    const api = new UnauthenticatedApi(client.apiHost)
    const tokenResponse = await Api.getClientToken(api, client)
    console.info('[lib.copy] Acquired oauth2 access_token')

    const { bundle } = await Api.getCopyBundle(
      api,
      appName,
      tokenResponse.access_token,
    )

    if (bundle === null) {
      console.info(`[lib.copy] No copy in response (bundle = null)`)
      return null
    }

    console.info(
      `[lib.copy] Successfully fetched bundle (containing ${getNumberOfKeysString(
        Object.keys(bundle).length,
      )})`,
    )

    return bundle
  } catch (err) {
    const msg =
      err instanceof ZodError || err instanceof Error ? err.message : err
    console.warn('[lib.copy] Failed to load copy.', msg)
    return null
  }
}

// private helpers

function getNumberOfKeysString(numOfKeys: number) {
  return numOfKeys === 1 ? `${numOfKeys} key` : `${numOfKeys} keys`
}

export function isBrowserRuntime() {
  return (
    typeof window !== 'undefined' ||
    typeof document !== 'undefined' ||
    typeof navigator !== 'undefined' ||
    typeof process === 'undefined'
  )
}
